import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { of, timer } from 'rxjs';
import { filter, switchMap, take, timeout } from 'rxjs/operators';
import { toLogLabel } from './log-label.util';
import { PerfChannel } from './models/perf-suggestion.model';
import { PerfAdvisorService } from './perf-advisor.service';
import { logPerf, logPerfAdvice } from './perf-logger.util';
import { SETTLE_GRACE_MS, SETTLE_MAX_WAIT_MS } from './perf-thresholds.const';

type TimedNavigationEvent =
  NavigationStart | NavigationEnd | NavigationCancel | NavigationError;

@Injectable({ providedIn: 'root' })
export class NavigationTimingService {
  private readonly router = inject(Router);
  private readonly advisor = inject(PerfAdvisorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly startTimesByNavigationId = new Map<number, number>();

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is TimedNavigationEvent =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.startTimesByNavigationId.set(event.id, performance.now());
          return;
        }

        const startMs = this.startTimesByNavigationId.get(event.id);
        this.startTimesByNavigationId.delete(event.id);

        if (startMs === undefined) {
          return;
        }

        const endMs = performance.now();
        const label = toLogLabel(event.url);

        if (event instanceof NavigationEnd) {
          // The timing line is logged straight away; advice waits until the
          // page's own data requests have settled (see below).
          logPerf(PerfChannel.NAV, label, endMs - startMs);
          this.adviseWhenSettled(label, startMs, endMs);
          return;
        }

        logPerf(
          PerfChannel.NAV,
          label,
          endMs - startMs,
          this.advisor.adviseNavigation({ url: label, startMs, endMs })
        );
      });
  }

  /**
   * Pages normally start loading their own data after NavigationEnd, so
   * judging the navigation at that moment would miss those requests. Wait for
   * them to start, then for none to be in flight (capped), and judge the load
   * up to the last request that finished.
   *
   * Requests still running at the cap are not counted, and unrelated
   * background requests that finish inside the window are - this is a
   * best-effort development aid, not an exact measurement.
   */
  private adviseWhenSettled(
    label: string,
    startMs: number,
    navigationEndMs: number
  ): void {
    timer(SETTLE_GRACE_MS)
      .pipe(
        switchMap(() =>
          this.advisor.inFlight$.pipe(filter((count) => count === 0))
        ),
        timeout({
          first: SETTLE_GRACE_MS + SETTLE_MAX_WAIT_MS,
          with: () => of(0),
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const settledEndMs = Math.max(
          navigationEndMs,
          this.advisor.latestHttpEndSince(startMs) ?? navigationEndMs
        );
        const suggestions = this.advisor.adviseNavigation({
          url: label,
          startMs,
          endMs: settledEndMs,
        });

        logPerfAdvice(
          `${label} (page settled after ${Math.round(settledEndMs - startMs)}ms)`,
          suggestions
        );
      });
  }
}
